import { useMutation } from '@tanstack/react-query';
import type {
    AddressContactDto,
    AddressDto,
    LocaleDto,
    PatientDtoGender,
    PatientDtoResidencyPermit,
} from '../../../api/generated/model';
import type { PersonLivingStatus } from '../person-controller/person-controller';
import { request } from '../../axios';

/**
 * These are the raw values coming from the form.
 * Keep this type close to the UI shape.
 */
export interface CreatePersonFormValues {
    firstName: string;
    lastName: string;
    birthDate: string;
    gender: PatientDtoGender | null;
    mobilePhoneNumber: string;
    privatePhoneNumber: string;
    workPhoneNumber: string;
    email: string;
    nationality: LocaleDto | null;
    origin: LocaleDto | null;
    arrival: string;
    asylumNumber: string;
    zemisNumber: string;
    languages: LocaleDto[];
    residencyPermit: PatientDtoResidencyPermit | null;
    zip: string;
    city: string;
    canton: string;
    street: string | null;
    additionalInformation: string | null;
    address: AddressDto | null;
    contact: AddressContactDto | null;
    salutation: string;
    livingStatus: PersonLivingStatus | null;
    note: string;
    bezugspersonen: any;
}

/**
 * API payload for POST /api/persons
 *
 * This matches the backend contract you showed from Postman.
 * Note:
 * - there is NO caseNumber
 * - address is nested as an object containing address/contact/from/referenceType
 */
export interface CreatePersonPayload {
    firstName: string;
    lastName: string;
    birthDate: string;
    gender: PatientDtoGender | null;
    mobilePhoneNumber: string | null;
    privatePhoneNumber: string | null;
    workPhoneNumber: string | null;
    email: string;
    nationality: LocaleDto | null;
    origin: LocaleDto | null;
    arrival: string | null;
    asylumNumber: string;
    zemisNumber: string;
    languages: LocaleDto[];
    residencyPermit: PatientDtoResidencyPermit | null;
    postalCode: string | null;
    city: string | null;
    state: string | null;
    street: string | null;
    additionalInformation: string | null;
    address: {
        address: AddressDto | null;
        contact: AddressContactDto | null;
        from: string;
        referenceType: 'PERSON_RESIDENCE';
    } | null;
    salutation: string | null;
    livingStatus?: PersonLivingStatus | null;
    note?: string | null;
    bezugspersonen: any;
}

/**
 * You can replace `unknown` with the generated response DTO later
 * if you already have one from your OpenAPI/orval generation.
 */
// export type CreatePersonResponse = ;

/**
 * Calls POST /api/persons using the shared request helper.
 * Authentication + CSRF headers are already handled globally
 * by your existing axios interceptor.
 */
export function createPerson(payload: CreatePersonPayload) {
    return request<string>({
        url: '/api/persons',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        data: payload,
    });
}

/**
 * React Query mutation for creating a person.
 */
export function useCreatePersonMutation() {
    return useMutation({
        mutationFn: createPerson,
    });
}